import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import Toast from '../../services/toast.service';
import ToolkitService from '../../services/toolkit.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
	standalone: true,
	selector: 'toolkit-assign',
	templateUrl: './toolkit-assign.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe, LoaderOverlayComponent]
})
export default class ToolkitAssignComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private route: ActivatedRoute = inject(ActivatedRoute);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private toast: Toast = inject(Toast);
	private sanitizer: DomSanitizer = inject(DomSanitizer);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	private readonly formatIcons: Record<string, string> = {
		psychoeducation:
			'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>',
		exercise:
			'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
		worksheet:
			'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
		'check-in':
			'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
		planner:
			'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>'
	};

	formatIcon(format?: string): SafeHtml {
		const key = (format || 'other').toString();
		const svg = this.formatIcons[key];
		if (svg) return this.sanitizer.bypassSecurityTrustHtml(svg);
		const letter = key.trim().charAt(0) || '?';
		return this.sanitizer.bypassSecurityTrustHtml('<span class="toolkit__resourceIconFallback">' + letter + '</span>');
	}

	ngOnInit() {
		this.routingService.validateAccess('toolkit').then(() => {
			this.onResolve();
		});
	}

	onResolve() {
		this.vm.loading = true;
		this.vm.error = false;
		this.vm.step = 1; // 1=client, 2=details, 3=done
		const rawKind = (this.route.snapshot.paramMap.get('kind') || 'resource').toString();
		this.vm.kind = rawKind === 'assessment' ? 'assessment' : 'resource';
		this.vm.itemId = (this.route.snapshot.paramMap.get('id') || '').toString();

		this.vm.clientSearch = '';
		this.vm.clients = [];
		this.vm.filteredClients = [];
		this.vm.selectedClient = null;
		this.vm.preselectClientId = (this.route.snapshot.queryParamMap.get('clientId') || '').toString();

		this.vm.note = '';
		this.vm.dueAt = '';
		this.vm.isBaseline = false;

		this.vm.item = null;
		this.vm.themes = [];
		this.vm.theme = null;

		this.vm.close = this.close.bind(this);
		this.vm.preview = this.preview.bind(this);
		this.vm.selectClient = this.selectClient.bind(this);
		this.vm.applyClientFilter = this.applyClientFilter.bind(this);
		this.vm.goStep = this.goStep.bind(this);
		this.vm.confirmAssignment = this.confirmAssignment.bind(this);

		if (!this.vm.itemId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		// Load catalogue (themes + for resource meta) and clients roster in parallel.
		this.toolkitService.getCatalogue({}, (catalogue) => {
			this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
			this.vm.loadingCatalogue = false;
			this.attachTheme();
		}, () => {
			this.vm.loadingCatalogue = false;
			this.vm.error = true;
		});

		this.toolkitService.getUserCustomers({}, (clients) => {
			this.vm.clients = Array.isArray(clients) ? clients : [];
			this.applyClientFilter();
			this.maybePreselectClient();
		});

		const onItemLoaded = (item) => {
			this.vm.item = item;
			this.attachTheme();
			this.vm.loading = false;
		};
		const onItemError = () => {
			this.vm.loading = false;
			this.vm.error = true;
		};

		if (this.vm.kind === 'assessment') {
			this.toolkitService.getAssessment({ id: this.vm.itemId }, onItemLoaded, onItemError);
		} else {
			this.toolkitService.getResource({ id: this.vm.itemId }, onItemLoaded, onItemError);
		}
	}

	private maybePreselectClient() {
		const raw = (this.vm?.preselectClientId || '').toString().trim();
		if (!raw) return;
		const idNum = Number(raw);
		const list: any[] = Array.isArray(this.vm?.clients) ? this.vm.clients : [];
		const found = list.find((c) => (c?.id != null ? Number(c.id) : NaN) === idNum) || null;
		if (!found) return;
		this.vm.preselectClientId = '';
		this.selectClient(found);
	}

	private attachTheme() {
		if (!this.vm.item || !Array.isArray(this.vm.themes)) return;
		const themeId = this.vm.item?.themeId || this.vm.item?.theme_id || this.vm.item?.theme;
		this.vm.theme = this.vm.themes.find((t) => (t?.id || t?.themeId) === themeId) || null;
	}

	close() {
		this.angularRoutingHelperService.setPath('/toolkit', false, false);
	}

	preview() {
		if (this.vm.kind === 'assessment') {
			this.angularRoutingHelperService.setPath(['/toolkit/assessment/preview', this.vm.itemId], false, false);
		} else {
			this.angularRoutingHelperService.setPath(['/toolkit/preview', this.vm.kind, this.vm.itemId], false, false);
		}
	}

	applyClientFilter() {
		const q = (this.vm.clientSearch || '').toString().trim().toLowerCase();
		const list = Array.isArray(this.vm.clients) ? this.vm.clients : [];
		this.vm.filteredClients = q
			? list.filter((c) => {
					const full = `${c?.firstname || ''} ${c?.lastname || ''}`.toLowerCase();
					const email = (c?.email || '').toLowerCase();
					return full.includes(q) || email.includes(q);
				})
			: list;
	}

	selectClient(c: any) {
		this.vm.selectedClient = c;
		this.vm.step = 2;
	}

	goStep(step: number) {
		if (step === 2 && !this.vm.selectedClient) return;
		this.vm.step = step;
	}

	confirmAssignment() {
		if (!this.vm.selectedClient || !this.vm.itemId) return;
		this.vm.submitting = true;

		const done = () => {
			this.vm.submitting = false;
		};

		if (this.vm.kind === 'assessment') {
			this.toolkitService.createAssessmentAssignment(
				{
					clientId: this.vm.selectedClient.id,
					assessmentId: this.vm.itemId,
					note: this.vm.note,
					dueAt: this.vm.dueAt || null,
					isBaseline: !!this.vm.isBaseline
				},
				() => {
					done();
					this.vm.step = 3;
					this.toast.showWithTranslation('Toolkit.Assign.Success');
				},
				() => {
					done();
					this.toast.showWithTranslation('Toolkit.Assign.Error');
				}
			);
			return;
		}

		this.toolkitService.createAssignment(
			{
				clientId: this.vm.selectedClient.id,
				resourceId: this.vm.itemId,
				note: this.vm.note,
				dueAt: this.vm.dueAt || null
			},
			() => {
				done();
				this.vm.step = 3;
				this.toast.showWithTranslation('Toolkit.Assign.Success');
			},
			() => {
				done();
				this.toast.showWithTranslation('Toolkit.Assign.Error');
			}
		);
	}
}
